import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Bell, Info, CheckCircle, AlertTriangle, AlertCircle, Eye, EyeOff, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import {
  announcementsService,
  Announcement,
  AnnouncementType,
  ANNOUNCEMENT_TYPE_LABELS,
  ANNOUNCEMENT_TYPE_BADGE,
  isAnnouncementExpired,
  targetRolesLabel,
  normalizeAnnouncementText,
} from '../../services/announcementsService';
import { Button } from '../../components/ui/Button';
import { formatDate } from '../../utils/helpers';

// Poradi stavu v seznamu: nejdriv to, co bezi, pak skryte (da se vratit)
// a uplne dole vyprsele - ty uz jsou historie.
const stavPoradi = (a: Announcement): number => {
  if (!a.isActive) return 1; // skryte spravcem
  return isAnnouncementExpired(a) ? 2 : 0;
};

// Poradi typu podle zavaznosti/barvy, shodne s ciselnikem typu.
const TYP_PORADI: Record<AnnouncementType, number> = {
  info: 0,
  success: 1,
  warning: 2,
  error: 3,
};

// Bez data vyprseni radime na konec - "nekonci datem" je dal nez jakekoli datum.
const vyprsiCas = (a: Announcement): number =>
  a.expiresAt ? new Date(a.expiresAt).getTime() : Number.MAX_SAFE_INTEGER;

const TYPE_ICON: Record<AnnouncementType, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

// Řazení klikem na záhlaví sloupce: vzestupně → sestupně → zpět výchozí pořadí.
type Sloupec = 'nazev' | 'typ' | 'komu' | 'priorita' | 'stav' | 'vyprsi' | 'vytvoreno';
type Razeni = { sloupec: Sloupec; smer: 1 | -1 } | null;

const POROVNANI: Record<Sloupec, (a: Announcement, b: Announcement) => number> = {
  nazev: (a, b) => a.title.localeCompare(b.title, 'cs'),
  typ: (a, b) => TYP_PORADI[a.type] - TYP_PORADI[b.type],
  komu: (a, b) => targetRolesLabel(a.targetRoles).localeCompare(targetRolesLabel(b.targetRoles), 'cs'),
  priorita: (a, b) => a.priority - b.priority,
  stav: (a, b) => stavPoradi(a) - stavPoradi(b),
  vyprsi: (a, b) => vyprsiCas(a) - vyprsiCas(b),
  vytvoreno: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
};

const vychoziPoradi = (a: Announcement, b: Announcement): number =>
  POROVNANI.stav(a, b) || POROVNANI.typ(a, b) || POROVNANI.vyprsi(a, b) || b.priority - a.priority;

export const AnnouncementsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => announcementsService.getAllAnnouncements(),
  });

  // Backend radi jen hrube (isActive, priorita, datum). Vychozi poradi je tu podle
  // stavu, typu, data vyprseni a nakonec priority - stav se navic pocita az na
  // klientovi (vyprseni plyne z data, ne ze sloupce). Klik na zahlavi ho prebije;
  // pri shode ve zvolenem sloupci rozhoduje vychozi poradi.
  const [razeni, setRazeni] = useState<Razeni>(null);
  const serazene = [...announcements].sort((a, b) =>
    razeni
      ? razeni.smer * POROVNANI[razeni.sloupec](a, b) || vychoziPoradi(a, b)
      : vychoziPoradi(a, b),
  );
  const prepniRazeni = (sloupec: Sloupec) =>
    setRazeni((r) => (!r || r.sloupec !== sloupec ? { sloupec, smer: 1 } : r.smer === 1 ? { sloupec, smer: -1 } : null));

  const Zahlavi: React.FC<{ sloupec: Sloupec; children: React.ReactNode }> = ({ sloupec, children }) => {
    const aktivni = razeni?.sloupec === sloupec;
    const Ikona = !aktivni ? ArrowUpDown : razeni!.smer === 1 ? ChevronUp : ChevronDown;
    return (
      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <button
          type="button"
          onClick={() => prepniRazeni(sloupec)}
          className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900 ${aktivni ? 'text-gray-900' : ''}`}
          title={aktivni ? (razeni!.smer === 1 ? 'Seřazeno vzestupně – klik: sestupně' : 'Seřazeno sestupně – klik: výchozí pořadí') : 'Seřadit podle tohoto sloupce'}
        >
          {children}
          <Ikona className={`w-3.5 h-3.5 ${aktivni ? '' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['announcements'] });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => announcementsService.deleteAnnouncement(id),
    onSuccess: invalidate,
    onError: (e: any) => alert(e.response?.data?.message || 'Chyba při mazání novinky'),
  });

  const toggleMutation = useMutation({
    mutationFn: (a: Announcement) =>
      a.isActive
        ? announcementsService.deactivateAnnouncement(a.id)
        : announcementsService.activateAnnouncement(a.id),
    onSuccess: invalidate,
    onError: (e: any) => alert(e.response?.data?.message || 'Chyba při změně stavu novinky'),
  });

  const handleDelete = (a: Announcement) => {
    if (window.confirm(`Opravdu chcete smazat novinku "${a.title}"?`)) {
      deleteMutation.mutate(a.id);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Správa novinek</h1>
          <p className="mt-2 text-gray-600">Vytvářejte a spravujte novinky pro uživatele</p>
        </div>
        <Button onClick={() => navigate('/announcements/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nová novinka
        </Button>
      </div>

      {announcements.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné novinky</h3>
          <p className="text-gray-600 mb-6">Začněte vytvořením první novinky</p>
          <Button onClick={() => navigate('/announcements/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Vytvořit novinku
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Zahlavi sloupec="nazev">Název</Zahlavi>
                <Zahlavi sloupec="typ">Typ</Zahlavi>
                <Zahlavi sloupec="komu">Zobrazit komu</Zahlavi>
                <Zahlavi sloupec="priorita">Priorita</Zahlavi>
                <Zahlavi sloupec="stav">Stav</Zahlavi>
                <Zahlavi sloupec="vyprsi">Vyprší</Zahlavi>
                <Zahlavi sloupec="vytvoreno">Vytvořeno</Zahlavi>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serazene.map((a) => {
                const expired = isAnnouncementExpired(a);
                const TypeIcon = TYPE_ICON[a.type];
                return (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={a.title}>{a.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{normalizeAnnouncementText(a.content)}</div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ANNOUNCEMENT_TYPE_BADGE[a.type]}`}>
                        <TypeIcon className="w-3 h-3" />
                        <span className="ml-1">{ANNOUNCEMENT_TYPE_LABELS[a.type]}</span>
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span
                        className={`inline-block max-w-[10rem] truncate px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          a.targetRoles?.length ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'
                        }`}
                        title={targetRolesLabel(a.targetRoles)}
                      >
                        {targetRolesLabel(a.targetRoles)}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{a.priority}</td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {/* Pořadí podmínek musí odpovídat řazení (stavPoradi): skryté správcem
                          je "Neaktivní" i tehdy, když k tomu uplynula platnost. Dřív se ptalo
                          nejdřív na vypršení, takže skrytá novinka ukazovala "Vypršelo"
                          a v tabulce to vypadalo, že se řadí náhodně. */}
                      {!a.isActive ? (
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                          title={
                            expired
                              ? 'Skryto správcem (platnost už také uplynula)'
                              : 'Skryto správcem – uživatelům se nezobrazuje'
                          }
                        >
                          Neaktivní
                        </span>
                      ) : expired ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Vypršelo</span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Aktivní</span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {a.expiresAt ? formatDate(a.expiresAt) : '—'}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(a.createdAt)}</td>
                    <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-0.5">
                        {/* Poradi akci: Upravit, (De)aktivovat, Smazat. */}
                        <button
                          type="button"
                          onClick={() => navigate(`/announcements/${a.id}/edit`)}
                          className="p-1.5 text-blue-600 hover:text-blue-700"
                          title="Upravit novinku"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleMutation.mutate(a)}
                          disabled={toggleMutation.isPending}
                          className="p-1.5 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                          title={a.isActive ? 'Deaktivovat novinku' : 'Aktivovat novinku'}
                        >
                          {a.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(a)}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 text-red-600 hover:text-red-700 disabled:opacity-50"
                          title="Smazat novinku"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
